import * as React from 'react';
import useCarouselContext from './CarouselContext';
import { ArrowLeft, ArrowRight, CircleFilled } from '@1debit/icons';
import { cn } from 'utils/styles';
import AutoplayProgressIndicator from 'components/shared/Carousel/AutoplayProgressIndicator';

export interface CarouselPaginationProps {
  total: number;
  current: number;
  onSelect: (index: number) => void;
  className?: string;
  isTimed?: boolean;
  timeDuration?: number;
  idPrefix?: string;

  testId?: string;
}

function CarouselPagination({
  total,
  current,
  onSelect,
  className,
  isTimed = false,
  timeDuration,
  idPrefix,
  testId,
}: CarouselPaginationProps) {
  const testFixture = `${testId ?? 'carousel'}`;

  return (
    <div data-testid={`${testFixture}-pagination`} className={cn('carousel-pagination flex items-center', className)}>
      <CarouselButton
        direction="prev"
        className={'text-(--pagination-arrow)'}
        {...(idPrefix != null && { id: `${idPrefix}_slider_back_arrow` })}
      />
      {Array.from({ length: total }, (_, index) => {
        const isActive = current === index;
        return (
          <button
            key={index}
            onClick={() => onSelect(index)}
            aria-label={`Go to slide ${index + 1}`}
            data-testid={`${testFixture}-pagination-dot-${index + 1}`}
            className={'p-1.75 cursor-pointer'}
            {...(idPrefix != null && { id: `${idPrefix}_dot_${index + 1}` })}>
            {isActive && isTimed ? (
              <AutoplayProgressIndicator duration={timeDuration ?? 0} />
            ) : (
              <ManualButtonContent isActive={isActive} isTimed={isTimed} />
            )}
          </button>
        );
      })}
      <CarouselButton
        direction="next"
        className={'text-(--pagination-arrow)'}
        {...(idPrefix != null && { id: `${idPrefix}_slider_forward_arrow` })}
      />
    </div>
  );
}

interface ButtonContentProps {
  isActive: boolean;
  isTimed?: boolean;
}

function ManualButtonContent({ isActive, isTimed }: ButtonContentProps) {
  return (
    <CircleFilled
      className={cn(
        'w-2.5 h-2.5 transition-colors',
        isActive ? 'text-(--pagination-slide-active)' : 'text-(--pagination-slide-default)',
        isTimed && 'text-(--pagination-timed-slide-default)',
      )}
    />
  );
}

export function CarouselButton({
  direction,
  className,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  direction: 'prev' | 'next';
}) {
  const { scrollPrev, scrollNext, canScrollPrev, canScrollNext } = useCarouselContext();
  const isNext = direction === 'next';
  const Icon = isNext ? ArrowRight : ArrowLeft;
  const canScroll = isNext ? canScrollNext : canScrollPrev;
  const onClick = isNext ? scrollNext : scrollPrev;

  return (
    <button
      disabled={!canScroll}
      onClick={onClick}
      aria-label={`${isNext ? 'Next' : 'Previous'} slide`}
      className={cn('disabled:opacity-50 disabled:cursor-not-allowed text-current hover:cursor-pointer', className)}
      {...props}>
      <Icon className="h-6 w-6" />
      <span className="sr-only">{isNext ? 'Next' : 'Previous'} slide</span>
    </button>
  );
}

export default CarouselPagination;
